import { useState, useCallback } from "react";
import "./OptimizedImage.css";

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  lazy?: boolean;
  priority?: boolean;
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * Simple image component with loading states
 * Shows loading placeholder until image loads from the provided URL
 * Updated: Simplified version without timeout logic
 */
export default function OptimizedImage({
  src,
  alt,
  className = "",
  width,
  height,
  lazy = true,
  priority = false,
  onLoad,
  onError,
}: OptimizedImageProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Handle empty or null src
  if (!src || src.trim() === "") {
    return (
      <div className={`optimized-image-container no-image ${className}`}>
        <div className="optimized-image-placeholder no-cover">
          <div className="no-cover-icon">📚</div>
          <div className="no-cover-text">No Cover</div>
        </div>
      </div>
    );
  }

  const handleLoad = useCallback(() => {
    setImageLoaded(true);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setImageError(true);
    onError?.();
  }, [onError]);

  const imageProps = {
    alt,
    className: `optimized-image ${className} ${
      imageLoaded ? "loaded" : "loading"
    }`,
    width,
    height,
    onLoad: handleLoad,
    onError: handleError,
    loading: lazy && !priority ? ("lazy" as const) : ("eager" as const),
    decoding: "async" as const,
  };

  if (imageError) {
    return (
      <div className={`optimized-image-container ${className}`}>
        <div className="optimized-image-placeholder image-error">
          <div className="error-icon">📷</div>
          <div className="error-text">Image unavailable</div>
        </div>
      </div>
    );
  }

  return (
    <div className="optimized-image-container">
      <img {...imageProps} src={src} />

      {/* Loading placeholder */}
      {!imageLoaded && (
        <div className="optimized-image-placeholder">
          <div className="placeholder-shimmer"></div>
        </div>
      )}
    </div>
  );
}
